import React from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../i18n";
import { LayoutDashboard, Users, FileEdit, BarChart3, Activity, LogOut, ContactRound, Image as ImageIcon, Type } from "lucide-react";

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-cyan-300 label-mono">CONECTANDO...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const nav_items = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: t("admin.nav.dashboard"), test: "nav-dashboard" },
    { to: "/admin/leads", icon: ContactRound, label: t("admin.nav.leads"), test: "nav-leads" },
    { to: "/admin/images", icon: ImageIcon, label: t("admin.nav.images"), test: "nav-images" },
    { to: "/admin/texts", icon: Type, label: t("admin.nav.texts"), test: "nav-texts" },
    { to: "/admin/content", icon: FileEdit, label: t("admin.nav.content"), test: "nav-content" },
    { to: "/admin/users", icon: Users, label: t("admin.nav.users"), test: "nav-users" },
    { to: "/admin/activity", icon: Activity, label: t("admin.nav.activity"), test: "nav-activity" },
    { to: "/admin/reports", icon: BarChart3, label: t("admin.nav.reports"), test: "nav-reports" },
  ];

  return (
    <div className="min-h-screen flex" data-testid="admin-shell">
      <aside className="w-64 border-r border-white/5 bg-black/40 backdrop-blur-md p-5 flex flex-col">
        <div className="mb-8">
          <img src="/xega-logo-new.png" alt="XEGA" className="h-9 w-auto drop-shadow-[0_0_10px_rgba(0,240,255,0.4)]" style={{ imageRendering: '-webkit-optimize-contrast' }} />
          <div className="label-mono text-[10px] mt-2">CONSOLE</div>
        </div>
        <nav className="space-y-1 flex-1">
          {nav_items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.test}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg transition ${isActive ? "bg-cyan-400/10 border border-cyan-400/30 text-cyan-300" : "border border-transparent text-white/70 hover:bg-white/5 hover:text-white"}`}>
              <it.icon className="w-4 h-4" /> <span className="font-tech text-sm">{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-4 glass-card chrome-border p-3">
          <div className="label-mono mb-1">SESSION</div>
          <div className="font-tech text-sm text-white truncate">{user.email}</div>
          <div className="text-xs text-cyan-300/70 uppercase tracking-wider mt-1">{user.role}</div>
          <div className="flex items-center gap-1 mt-3 bg-black/40 border border-white/10 rounded-full p-1 w-fit">
            <button onClick={()=>setLang("es")} className={`px-2 py-0.5 text-[10px] rounded-full ${lang==="es"?"bg-cyan-400 text-black":"text-white/60"}`}>ES</button>
            <button onClick={()=>setLang("en")} className={`px-2 py-0.5 text-[10px] rounded-full ${lang==="en"?"bg-cyan-400 text-black":"text-white/60"}`}>EN</button>
          </div>
          <button onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="logout-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-rose-400/40 hover:text-rose-300 transition text-sm">
            <LogOut className="w-4 h-4" /> {t("admin.nav.logout")}
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-8">
        <Outlet />
      </main>
    </div>
  );
}
